import type { Edge, Node } from '@xyflow/react'
import {
  getParameterValue,
  setParameterValue,
} from '@/features/backtest/components/nodes/universal-node-helpers'
import { parseTimeframeToMinutes } from '@/features/backtest/utils/price-data-ownership'
import { connectedUniverseIds } from '@/features/backtest/utils/single-universe'

/**
 * The ticker and day a strategy previews and a single run runs (Chris,
 * 2026-10-28): the connected Stock Selection owns them, under its Preview
 * heading (they feed the chart's automatic preview); a strategy with none
 * connected keeps them on its main Price Data, as every strategy did before.
 * Spec: docs/superpowers/specs/2026-10-28-stock-selection-run-day-and-spine-design.md.
 *
 * The only reader. Price Data's own `symbol` / `targetDate` are what a strategy
 * without a connected Stock Selection runs on, and nothing else: read through
 * `strategyTickerDay`, never off a Price Data node.
 */
export interface RunTickerDay {
  symbol: string
  /** YYYY-MM-DD, or '' when nothing holds a day. */
  targetDate: string
  /** Who owns the values. */
  from: 'stock-selection' | 'price-data'
  /** The node to write a change to: the Stock Selection, else the main Price Data. */
  nodeId: string | null
}

/** The structural minimum, so both canvas nodes and stored GraphNodes fit. */
interface DayNode {
  id: string
  type?: string
  data?: unknown
}
interface DayEdge {
  source: string
  target: string
  data?: unknown
}

const parametersOf = (node: DayNode): unknown =>
  (node.data as Record<string, unknown> | undefined)?.parameters

const textParam = (node: DayNode | undefined, name: string): string => {
  const value = node ? getParameterValue(parametersOf(node), name) : undefined
  return value === undefined || value === null ? '' : String(value)
}

/**
 * The Price Data a strategy runs on: the primary one, else the finest
 * timeframe. Reference Data never counts: its symbol (SPY beside the traded
 * ticker) is deliberately not the run's.
 */
export function mainPriceData<N extends DayNode>(nodes: readonly N[] | undefined): N | undefined {
  const prices = (nodes ?? []).filter((n) => n.type === 'priceData')
  const primary = prices.find(
    (n) => (n.data as { isPrimary?: unknown } | undefined)?.isPrimary === true,
  )
  if (primary) return primary
  let best: N | undefined
  let bestMinutes = Infinity
  for (const node of prices) {
    const minutes = parseTimeframeToMinutes(textParam(node, 'timeframe') || '1D')
    if (minutes < bestMinutes) {
      best = node
      bestMinutes = minutes
    }
  }
  return best
}

/** The Stock Selection feeding the graph, if one is wired in. */
function connectedStockSelection<N extends DayNode>(
  nodes: readonly N[],
  edges: readonly DayEdge[],
): N | undefined {
  const [id] = connectedUniverseIds(nodes as unknown as Node[], edges as unknown as Edge[])
  return id === undefined ? undefined : nodes.find((n) => n.id === id)
}

/**
 * The run's ticker and day: the connected Stock Selection's, else the main
 * Price Data's. A Stock Selection field left empty (one saved before it owned
 * the day, which the load fills in) reads the main Price Data's, so such a
 * strategy runs exactly as it did.
 */
export function strategyTickerDay(
  nodes: readonly DayNode[] | undefined,
  edges: readonly DayEdge[] | undefined,
): RunTickerDay {
  const price = mainPriceData(nodes)
  const universe = nodes && edges ? connectedStockSelection(nodes, edges) : undefined
  const read = (name: 'symbol' | 'targetDate') =>
    textParam(universe, name) || textParam(price, name)
  return {
    symbol: read('symbol'),
    targetDate: read('targetDate').slice(0, 10),
    from: universe ? 'stock-selection' : 'price-data',
    nodeId: universe?.id ?? price?.id ?? null,
  }
}

function withParams<N extends DayNode>(node: N, values: Record<string, string>): N {
  let parameters = parametersOf(node)
  let changed = false
  for (const [name, value] of Object.entries(values)) {
    if (!value || textParam(node, name) === value) continue
    parameters = setParameterValue(parameters, name, value)
    changed = true
  }
  if (!changed) return node
  return { ...node, data: { ...(node.data as Record<string, unknown>), parameters } }
}

/**
 * Every feed on the run's ticker and day. The engine still reads them off
 * Price Data, so the send step writes them onto its copy of each one. One
 * strategy runs one sample, so a second timeframe shares the ticker and day
 * rather than drifting onto another. Reference Data takes the day only: its
 * own symbol is the point of it. Empty values are not written; nodes already
 * on the day stay the same objects, and with none to change the array does.
 */
export function withRunTickerDay<N extends DayNode>(
  nodes: N[],
  day: Pick<RunTickerDay, 'symbol' | 'targetDate'>,
): N[] {
  let changed = false
  const next = nodes.map((node) => {
    const values: Record<string, string> | null =
      node.type === 'priceData'
        ? { symbol: day.symbol, targetDate: day.targetDate }
        : node.type === 'marketData'
          ? { targetDate: day.targetDate }
          : null
    if (!values) return node
    const stamped = withParams(node, values)
    if (stamped !== node) changed = true
    return stamped
  })
  return changed ? next : nodes
}

/**
 * The one-time move on load: a connected Stock Selection with no symbol or
 * day takes the main Price Data's, so the run keeps its ticker and day and the
 * Preview rows show them. Fills fields only. A strategy with no connected
 * Stock Selection is returned as it came: opening a strategy never adds a
 * node or a wire, because the page autosaves what it opened.
 */
export function hoistRunTickerDay<N extends DayNode>(nodes: N[], edges: readonly DayEdge[]): N[] {
  const universe = connectedStockSelection(nodes, edges)
  const price = mainPriceData(nodes)
  if (!universe || !price) return nodes
  const missing: Record<string, string> = {}
  for (const name of ['symbol', 'targetDate'] as const) {
    if (!textParam(universe, name)) missing[name] = textParam(price, name)
  }
  const filled = withParams(universe, missing)
  return filled === universe ? nodes : nodes.map((n) => (n.id === universe.id ? filled : n))
}
